// ============================================================================
// Safe Care - Rachat / Sortie de contrat (côté agent)
// Module : features/assistance_sociale/pages
// Charte : .tab / .formTitle / .bG / .bt_add / .tabItem / .tabElement / InputForm
// Pattern : identique à InterruptionSouscriptions.tsx (formulaire dans Tab + tableau en dessous)
// ============================================================================

import { useState, useEffect } from 'react';
import { Table, Spin, Button, Form, Space, Modal, Input, InputNumber } from 'antd';
import { Box, Typography } from '@mui/material';
import { ColumnsType } from 'antd/es/table';
import SweetAlert from 'sweetalert2';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../Models/Utilisateur';
import InputForm from '../../../Components/InputT';
import {
  useGetAllSouscriptions,
  useCreateRachat,
} from '../hooks';
import { SouscriptionSafeCare, Rachat } from '../types';

const RachatPage = () => {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));

  const { data: souscriptions, isLoading } = useGetAllSouscriptions('ACTIVE');
  const createRachat = useCreateRachat();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [Data, SetData] = useState<SouscriptionSafeCare[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedSouscription, setSelectedSouscription] = useState<SouscriptionSafeCare | null>(null);
  const [modalConfirm, setModalConfirm] = useState(false);

  // Etats des boutons d'action (charte cSAL)
  const [ajoutActve, setAjoutActif] = useState(false);
  const [modifActve, setModifActve] = useState(true);
  const [enregActve, setEnregActve] = useState(true);
  const [supActive, setSupActive] = useState(true);
  const [impActive, setImpActive] = useState(true);
  const [actuActive, setActActive] = useState(true);
  const [annulActive, setAnnulActive] = useState(true);
  const [consulActive, setConsulActive] = useState(false);

  useEffect(() => {
    if (souscriptions) SetData(souscriptions);
  }, [souscriptions]);

  function initialiazeButton() {
    setAjoutActif(false);
    setModifActve(true);
    setEnregActve(true);
    setSupActive(true);
    setImpActive(true);
    setActActive(true);
    setAnnulActive(true);
    setConsulActive(false);
  }

  const onSelectChange = (newSelectedRowKeys: any, selectedRows: any) => {
    setSelectedRowKeys(newSelectedRowKeys);
    const record = selectedRows?.[0] ?? Data.find((d: any) => d.IdSouscription === newSelectedRowKeys?.[0]);
    if (record) {
      form.setFieldsValue(record);
      setSelectedSouscription(record);
    }
    // Activer le bouton Rachater et Annuler
    setEnregActve(false);
    setAnnulActive(false);
    setConsulActive(true);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  function consult() {
    if (selectedSouscription) {
      form.setFieldsValue(selectedSouscription);
    }
    setAnnulActive(false);
    setModifActve(false);
  }

  function handleAnulle() {
    form.resetFields();
    initialiazeButton();
    setSelectedRowKeys([]);
    setSelectedSouscription(null);
  }

  function actualiser() {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      if (souscriptions) SetData(souscriptions);
      SweetAlert.fire({ title: 'Liste actualisée', icon: 'success', timer: 1000 });
    }, 500);
  }

  function openModalRachat() {
    if (!selectedSouscription) {
      SweetAlert.fire({ title: 'Sélectionnez une souscription', icon: 'warning' });
      return;
    }
    // Avertissement : le rachat peut ne pas être autorisé selon la version (RachatAutorise)
    // On permet quand même l'action, le backend fera la vérification finale
    setModalConfirm(true);
  }

  const confirmRachat = () => {
    const values = form.getFieldsValue() as Rachat;
    if (!values.Montant || values.Montant <= 0) {
      SweetAlert.fire({ title: 'Indiquez un montant valide', icon: 'warning' });
      return;
    }
    if (!values.DateRachat) {
      SweetAlert.fire({ title: 'Indiquez la date de rachat', icon: 'warning' });
      return;
    }
    setLoading(true);
    const payload: Rachat = {
      IdSouscription: selectedSouscription?.IdSouscription!,
      Numcli: selectedSouscription?.Numcli ?? '',
      Montant: values.Montant,
      DateRachat: values.DateRachat,
      Motif: values.Motif,
      Codope: values.Codope,
      UtiSai: user.coduti ?? '',
    };
    createRachat.mutate(payload, {
      onSuccess: (res: any) => {
        setLoading(false);
        setModalConfirm(false);
        if (res?.StatutCode === 200) {
          SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
          handleAnulle();
        } else {
          SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Réponse vide', icon: 'warning' });
        }
      },
      onError: (err: any) => {
        setLoading(false);
        SweetAlert.fire({ title: 'Erreur', text: err?.message ?? 'Erreur', icon: 'error' });
      },
    });
  };

  const onFinish = (values: any) => {
    openModalRachat();
  };

  const columns: ColumnsType<SouscriptionSafeCare> = [
    { title: 'Client', dataIndex: 'Numcli', width: 100 },
    { title: 'Nom', dataIndex: 'NOMCLI', width: 120 },
    { title: 'Produit', dataIndex: 'NomProduit', width: 150 },
    { title: 'Version', dataIndex: 'NumeroVersion', width: 80 },
    { title: 'Compte', dataIndex: 'NumCptDebit', width: 120 },
    { title: 'Cotisation', dataIndex: 'MontantCotisation', width: 100 },
    { title: 'Date effet', dataIndex: 'DateEffet', width: 120 },
    { title: 'Date fin', dataIndex: 'DateFin', width: 120 },
    { title: 'Statut', dataIndex: 'Statut', width: 100 },
  ];

  // ==================== FORMULAIRE RACHAT ====================
  const FormRachat = () => {
    return (
      <div className="tabItem">
        <div>
          <Box fontStyle="inherit" className="border border-left" width="85%" position="absolute" sx={{ p: 1 }}>
            <legend>Rachat / Sortie de contrat</legend>
            <Box display="flex" fontStyle="inherit" sx={{ mb: 1 }}>
              <Box fontStyle="inherit">
                <Form.Item name="IdSouscription" hidden>
                  <input type="hidden" />
                </Form.Item>
                <Form.Item name="Numcli" hidden>
                  <input type="hidden" />
                </Form.Item>
                <InputForm width={9} label="Client" name="NOMCLI" type="label" />
                <InputForm width={9} label="Produit" name="NomProduit" type="label" />
                <InputForm width={9} label="Version" name="NumeroVersion" type="label" />
              </Box>
              <hr />
              <Box fontStyle="inherit">
                <InputForm width={9} label="Compte" name="NumCptDebit" type="label" />
                <InputForm width={9} label="Cotisation" name="MontantCotisation" type="label" />
                <InputForm width={9} label="Date effet" name="DateEffet" type="label" />
                <InputForm width={9} label="Statut" name="Statut" type="label" />
              </Box>
            </Box>
            <hr />
            <Box fontStyle="inherit" className="border border-left" sx={{ mt: 2 }}>
              <legend>Détails du rachat</legend>
              <Box display="flex" fontStyle="inherit" sx={{ mb: 1 }}>
                <Box fontStyle="inherit">
                  <Form.Item
                    label="Montant"
                    name="Montant"
                    labelCol={{ span: 9 }}
                    rules={[{ required: true, message: 'Montant requis' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step="0.01"
                      placeholder="Montant du rachat"
                    />
                  </Form.Item>
                  <InputForm width={9} label="Date rachat" name="DateRachat" type="date" />
                </Box>
                <hr />
                <Box fontStyle="inherit">
                  <InputForm width={9} label="Motif" name="Motif" type="TextArea" row={3} />
                  <Form.Item
                    label="Codope"
                    name="Codope"
                    labelCol={{ span: 9 }}
                    rules={[{ required: true, message: 'Code opération requis' }]}
                  >
                    <Input placeholder="Code opération de rachat" />
                  </Form.Item>
                </Box>
              </Box>
              {/* Avertissement RachatAutorise */}
              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography color="warning.main" variant="body2">
                  ⚠ Le rachat peut ne pas être autorisé pour la version du produit (champ RachatAutorise).
                  Le backend effectuera la vérification finale.
                </Typography>
              </Box>
            </Box>
          </Box>
        </div>
      </div>
    );
  };

  return (
    <div className="tab safeCarePage">
      <Typography align="center" className="formTitle">
        <b>Rachat / Sortie de contrat Safe Care</b>
      </Typography>
      <Spin spinning={isLoading || loading} size="large" style={{ color: '#116599' }}>
        <Form
          disabled={!ajoutActve && !consulActive}
          autoComplete="off"
          labelAlign="left"
          form={form}
          onFinish={onFinish}
          onFinishFailed={() => {}}
          style={{ minWidth: 'auto', maxWidth: 'auto' }}
        >
          <div className="bG">
            <legend>
              <Typography color="#116599">Actions</Typography>
            </legend>
            <Form.Item shouldUpdate className="bouttong">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={consulActive}
                  onClick={consult}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Consulter
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={enregActve}
                  onClick={openModalRachat}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Racheter
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={actuActive}
                  onClick={actualiser}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Actualiser
                </Button>
                <Button
                  size="large"
                  disabled={annulActive}
                  onClick={handleAnulle}
                  className="bt_add"
                  type="ghost"
                  htmlType="reset"
                  block
                >
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className="clientP">
            <div>
              {FormRachat()}
            </div>
          </div>
        </Form>
        <br />
        <br />
        {/* Tableau des souscriptions actives - radio selection (hors Form pour permettre le clic radio) */}
        <Box display="flex" fontStyle="inherit" className="border-left tabElement" sx={{ p: 2, my: 5 }}>
          <Spin spinning={isLoading || loading}>
            <div>
              <Table
                size="small"
                style={{ width: '100%' }}
                rowKey="IdSouscription"
                rowSelection={{ ...rowSelection, type: 'radio' }}
                columns={columns}
                dataSource={Data}
                scroll={{ y: 250, x: 10 }}
                pagination={{ defaultPageSize: 10, position: ['bottomRight'] }}
              />
            </div>
          </Spin>
        </Box>
      </Spin>

      <Modal
        title="Confirmation du rachat"
        open={modalConfirm}
        onOk={confirmRachat}
        onCancel={() => setModalConfirm(false)}
        okText="Confirmer le rachat"
        cancelText="Annuler"
        okButtonProps={{ loading: loading }}
        className="safeCareModal"
      >
        {selectedSouscription && (
          <Box sx={{ mb: 2 }}>
            <p>Client : <b>{selectedSouscription.NOMCLI} ({selectedSouscription.Numcli})</b></p>
            <p>Produit : <b>{selectedSouscription.NomProduit}</b></p>
            <p>Montant : <b>{form.getFieldValue('Montant')} FCFA</b></p>
            <p>Date rachat : <b>{form.getFieldValue('DateRachat')}</b></p>
            <Typography color="warning.main" variant="body2" sx={{ mt: 1 }}>
              ⚠ Vérifiez que le rachat est autorisé pour cette version (RachatAutorise).
            </Typography>
          </Box>
        )}
      </Modal>
    </div>
  );
};

export default RachatPage;